import React, { useEffect, useState } from "react";
import { Lock, Loader2, CheckCircle2, AlertTriangle } from "lucide-react";
import * as api from "../utils/api";

const inputCls =
  "w-full px-3.5 py-2.5 bg-slate-50 border border-slate-200 focus:border-indigo-500 focus:bg-white rounded-xl outline-none transition-all text-base sm:text-xs text-slate-850";

/** Полноэкранная страница по ссылке из письма-приглашения (?invite=token): сотрудник
 * задаёт себе пароль сам, вместо того чтобы получать его из рук администратора. */
export default function InviteSetPassword({ token, onDone }: { token: string; onDone: () => void }) {
  const [state, setState] = useState<"loading" | "form" | "done" | "invalid">("loading");
  const [username, setUsername] = useState<string | null>(null);
  const [password, setPassword] = useState("");
  const [password2, setPassword2] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    api.inviteInfo(token)
      .then((info) => { if (alive) { setUsername(info.username); setState("form"); } })
      .catch(() => { if (alive) setState("invalid"); });
    return () => { alive = false; };
  }, [token]);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (password.length < 6) { setError("Пароль должен быть не короче 6 символов"); return; }
    if (password !== password2) { setError("Пароли не совпадают"); return; }
    setBusy(true);
    try {
      await api.inviteSetPassword(token, password);
      setState("done");
    } catch (err: any) {
      setError(err.message || "Не удалось установить пароль");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6">
      <div className="w-full max-w-sm bg-white p-6 rounded-2xl border border-slate-100 shadow-lg shadow-slate-100/50 space-y-5">
        <div className="space-y-1.5 text-center">
          <div className="w-10 h-10 rounded-xl bg-indigo-50 text-indigo-600 flex items-center justify-center mx-auto">
            <Lock className="w-5 h-5" />
          </div>
          <h3 className="font-extrabold text-slate-900 tracking-tight font-display">Приглашение в ANYskin</h3>
        </div>

        {state === "loading" && (
          <div className="flex items-center justify-center gap-2 text-sm text-slate-500 py-6">
            <Loader2 className="w-4 h-4 animate-spin" /> Проверяем ссылку…
          </div>
        )}

        {state === "invalid" && (
          <div className="text-center space-y-3">
            <AlertTriangle className="w-8 h-8 text-amber-500 mx-auto" />
            <p className="text-sm text-slate-700">
              Ссылка недействительна, устарела или уже использована.
            </p>
            <p className="text-[11px] text-slate-500">
              Попросите администратора организации выслать приглашение ещё раз.
            </p>
            <button type="button" onClick={onDone}
              className="w-full py-2.5 bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold text-xs uppercase tracking-wide rounded-xl transition-all cursor-pointer">
              На главную
            </button>
          </div>
        )}

        {state === "form" && (
          <form onSubmit={submit} className="space-y-3.5">
            <p className="text-xs text-slate-500 text-center">
              Здравствуйте{username ? `, ${username}` : ""}! Придумайте пароль для входа.
            </p>
            {error && (
              <div className="p-3 bg-rose-50 border border-rose-100 text-rose-800 text-xs rounded-lg font-medium">
                {error}
              </div>
            )}
            <div className="space-y-1">
              <label htmlFor="invite-pwd" className="text-[11px] font-semibold text-slate-600">Пароль</label>
              <input id="invite-pwd" type="password" autoComplete="new-password" autoFocus
                value={password} onChange={(e) => setPassword(e.target.value)}
                className={inputCls} required minLength={6} maxLength={128} />
            </div>
            <div className="space-y-1">
              <label htmlFor="invite-pwd2" className="text-[11px] font-semibold text-slate-600">Повторите пароль</label>
              <input id="invite-pwd2" type="password" autoComplete="new-password"
                value={password2} onChange={(e) => setPassword2(e.target.value)}
                className={inputCls} required minLength={6} maxLength={128} />
            </div>
            <button type="submit" disabled={busy}
              className="w-full py-2.5 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-60 text-white font-bold text-xs uppercase tracking-wide rounded-xl transition-all cursor-pointer inline-flex items-center justify-center gap-2">
              {busy && <Loader2 className="w-3.5 h-3.5 animate-spin" />} Задать пароль и войти
            </button>
          </form>
        )}

        {state === "done" && (
          <div className="text-center space-y-3">
            <CheckCircle2 className="w-8 h-8 text-emerald-500 mx-auto" />
            <p className="text-sm text-slate-700">
              Пароль установлен{username ? <> — логин <strong className="font-mono text-slate-900">{username}</strong></> : ""}.
            </p>
            <button type="button" onClick={onDone}
              className="w-full py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white font-bold text-xs uppercase tracking-wide rounded-xl transition-all cursor-pointer">
              Войти
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
